import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Form, Radio, Spin} from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import CreditCardForm from "./CreditCardForm";
import InputMoney from "./InputMoney";
import StoredCreditCardForm from "./StoredCreditCardForm";
import {getCreditCards, oneTimePayment} from "../../helpers/creditCard";
import {messageType, showMessages} from "../../helpers/message";
import {greaterThanOrEqualOne} from "../../helpers/text";

const ChargeCreditCardForm = ({toggleRefreshBalances, refetchStoredCard, setRefetchStoredCard}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const [loading, setLoading] = useState(true);
    const [cardType, setCardType] = useState(null);
    const [creditCard, setCreditCard] = useState(null);
    const [disableSavedCardOption, setDisableSavedCardOption] = useState(null);

    // region form functions
    const [form] = Form.useForm();

    const onPaymentTypeChange = (e) => {
        switch (e.target.value) {
            case 'saved':
                setCardType('saved');
                break;
            case 'separate':
                setCardType('separate');
                break;
            default:
                break;
        }
    }

    const getCCForm = () => {
        let form;

        switch (cardType) {
            case 'separate':
                form = <CreditCardForm/>;
                break;
            case 'saved':
                form = <StoredCreditCardForm creditCard={creditCard}/>;
                break;
            default:
                form = null;
                break;
        }

        return form;
    }

    const handleFinish = async (values) => {
        const amount = values['Amount'];
        const cardNumber = values['Card Number'] ? values['Card Number'].toString() : null;
        const expiryYear = values['Year'];
        const expiryMonth = values['Month'];
        const cvc = values['CVC'] ? values['CVC'] : null;
        const savedCard = cardType === 'saved';
        let expiryDateString = null;

        // Do expiry date check with separate cc form
        if (cardType === 'separate') {
            const dateFormat = 'YYYY-M-D';
            expiryDateString = `${expiryYear}-${expiryMonth}-1`;
            const expiryDate = dayjs(expiryDateString, dateFormat).tz();
            const firstDayOfCurrentMonth = dayjs().tz().startOf('month').utc();

            if (expiryDate.utc().isBefore(firstDayOfCurrentMonth.utc())) {
                showMessages('The card has expired.', messageType.error);
                return;
            }
        }

        try {
            setLoading(true);

            const abortSignal = abortSignalRef.current;
            await oneTimePayment(amount, cardNumber, expiryDateString, cvc, savedCard, abortSignal);

            showMessages('Payment has successfully been applied to account.', messageType.success);
            toggleRefreshBalances(true);
            setLoading(false);
            form.resetFields();
            form.setFieldsValue({
                'Payment Type': cardType
            });
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
        }
    };

    //endregion

    const fetchStoredCC = useCallback(async (abortSignal) => {
        try {
            const response = await getCreditCards({}, abortSignal);

            // For now only single cc can be stored
            const creditCards = response.data;
            let creditCard = null;

            if (creditCards.length > 0) {
                creditCard = creditCards[0];
                const expiryDate = dayjs(creditCard.expiry).tz();
                creditCard.expiry = expiryDate.format('MM') + ' / ' + expiryDate.format('YY');
                creditCard.type = creditCard.type.toUpperCase();

                setCreditCard(creditCard);
                setCardType('saved');
                form.setFieldsValue({
                    'Payment Type': 'saved'
                });
                setDisableSavedCardOption(false);
            } else {
                setCardType('separate');
                form.setFieldsValue({
                    'Payment Type': 'separate'
                });
                setCreditCard(null);
                setDisableSavedCardOption(true);
            }

            setLoading(false);
        } catch (e) {
            showMessages(e.message, messageType.error);
            setLoading(false);
            if(e.status_code === 404) window.location.replace('/404');
        }
    }, [form]);

    useEffect(() => {
        if (refetchStoredCard === true) {
            const abortSignal = abortSignalRef.current;
            fetchStoredCC(abortSignal);
            setRefetchStoredCard(false);
        }
    }, [refetchStoredCard, setRefetchStoredCard, fetchStoredCC]);

    useEffect(() => {
        const abortSignal = abortSignalRef.current;
        if (creditCard === null) {
            fetchStoredCC(abortSignal);
        }
    }, [creditCard, fetchStoredCC]);

    // Cleanup effect when component unmounts.
    useEffect(() => {
        const abortController = abortControllerRef.current;
        return function abort() {
            abortController.abort();
        }
    }, []);

    return (
        <Spin spinning={loading}>
            <Form
                form={form}
                layout="vertical"
                name="charge-credit-card"
                onFinish={handleFinish}
            >
                <Form.Item
                    name="Amount"
                    label="Amount"
                    rules={[{type: 'number', required: true, min: 1}]}
                    help={greaterThanOrEqualOne}
                >
                    <InputMoney />
                </Form.Item>
                <Form.Item
                    name="Payment Type"
                    label="Payment Type"
                    rules={[{required: true}]}
                >
                    <Radio.Group
                        name="paymentType"
                        onChange={onPaymentTypeChange}
                    >
                        <Radio value="saved" className="radio-button" disabled={disableSavedCardOption}>
                            With saved card
                        </Radio>
                        <Radio
                            value="separate"
                            className="radio-button"
                        >
                            With separate card
                        </Radio>
                    </Radio.Group>
                </Form.Item>
                {getCCForm()}
                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        Complete Payment
                    </Button>
                </Form.Item>
            </Form>
        </Spin>
    );
};

export default ChargeCreditCardForm;

ChargeCreditCardForm.propTypes = {
    toggleRefreshBalances: PropTypes.func.isRequired,
    refetchStoredCard: PropTypes.bool.isRequired,
    setRefetchStoredCard: PropTypes.func.isRequired,
};
